import React, { useEffect, useRef, useState } from "react";
import { Button, message, Modal } from "antd";
import { CameraOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { BrowserMultiFormatReader } from "@zxing/library";

const BarcodeScanner = ({ visible, onClose, onScan, zIndex = 1000 }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [hasPermission, setHasPermission] = useState(null);

  useEffect(() => {
    if (visible) {
      startScanning();
    } else {
      stopScanning();
    }

    return () => {
      stopScanning();
    };
  }, [visible]);

  const startScanning = async () => {
    try {
      // First, explicitly request camera permission
      // This triggers the browser's permission prompt on iOS Safari
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }, // Prefer back camera on mobile
      });

      // Stop the test stream immediately - we just needed it to trigger permission
      stream.getTracks().forEach((track) => track.stop());

      // Now proceed with the barcode reader
      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;

      const videoInputDevices = await codeReader.listVideoInputDevices();

      if (videoInputDevices.length === 0) {
        message.error(t("notifications.no_camera_found"));
        setHasPermission(false);
        return;
      }

      // Prefer back camera on mobile devices
      let selectedDeviceId = videoInputDevices[0].deviceId;
      const backCamera = videoInputDevices.find(
        (device) => device.label.toLowerCase().includes("back") || device.label.toLowerCase().includes("environment"),
      );
      if (backCamera) {
        selectedDeviceId = backCamera.deviceId;
      }

      setHasPermission(true);
      setIsScanning(true);

      codeReader.decodeFromVideoDevice(selectedDeviceId, videoRef.current, (result, err) => {
        if (result) {
          const scannedValue = result.getText();
          onScan(scannedValue);
          stopScanning();
          onClose();
        }
        if (err && err.name !== "NotFoundException") {
          console.error("Barcode scanning error:", err);
        }
      });
    } catch (error) {
      console.error("Error starting barcode scanner:", error);

      // More specific error messages
      if (error.name === "NotAllowedError") {
        message.error(t("notifications.camera_access_denied"));
      } else if (error.name === "NotFoundError") {
        message.error(t("notifications.no_camera_found"));
      } else {
        message.error(t("notifications.camera_access_denied"));
      }

      setHasPermission(false);
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
      codeReaderRef.current = null;
    }
    setIsScanning(false);
  };

  const handleClose = () => {
    stopScanning();
    onClose();
  };

  return (
    <Modal
      title={t("scan_barcode")}
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose} icon={<CloseOutlined />}>
          {t("close")}
        </Button>,
      ]}
      width={600}
      centered
      zIndex={zIndex}
      destroyOnClose
    >
      <div style={{ textAlign: "center" }}>
        {hasPermission === false && (
          <div style={{ padding: "40px 20px" }}>
            <CameraOutlined style={{ fontSize: 48, color: "#ccc", marginBottom: 16 }} />
            <p>{t("camera_permission_required")}</p>
          </div>
        )}

        {hasPermission !== false && (
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: 500,
              margin: "0 auto",
              backgroundColor: "#000",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <video
              ref={videoRef}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
              }}
            />
            {isScanning && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "80%",
                  height: "60%",
                  border: "2px solid #52c41a",
                  borderRadius: 8,
                  pointerEvents: "none",
                }}
              />
            )}
          </div>
        )}

        {isScanning && (
          <p style={{ marginTop: 16, color: "#52c41a" }}>
            <CameraOutlined /> {t("scanning_barcode")}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default BarcodeScanner;
